import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import {
  AnalysisMetrics,
  buildAnalysisMetrics,
  buildStageMetrics,
  collectPendingDocumentIds,
  StageMetrics,
} from '@features/dashboard/document-analysis-company/helpers/document-analysis-metrics';
import { DocumentAnalysisPresentationService } from '@features/dashboard/document-analysis-company/services/document-analysis-presentation.service';
import {
  OnboardingCompanyInformation,
  OnboardingProcessStage,
  OnboardingProcessStageRequiredDocuments,
  OnboardingProcessStageSubmittedDocuments,
} from '@interfaces/a-organizar/onboardingCompanyInformation';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { DashboardService } from '@services/http/dashboard.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { DocumentStatus } from '@src/app/core/enums/document-status.enum';
import { BulkApproveDocumentsResult } from '@src/app/core/models/company-onboarding/bulk-approve-documents-result';
import { CompanyOnboardingRequest } from '@src/app/core/models/company-onboarding/company-onboarding-request';
import { OnboardingDocumentsTranslationService } from '@src/app/features/onboarding/v1/components/helpers/translate-helper';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import {
  ModalConfirmComponent,
  ModalConfirmDetailItem,
} from '@src/app/shared/components/modal/modal-confirm/modal-confrm.component';
import { AppState } from '@src/app/state';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-document-analysis-company',
  standalone: false,
  templateUrl: './document-analysis-company.component.html',
  styleUrls: ['./document-analysis-company.component.scss'],
})
export class DocumentAnalysisCompanyComponent extends VaultBaseComponent implements OnInit {
  // Abrir modal SEMPRE por estas referências (`dialogRef.open(...)`), nunca por
  // `confirmDialogService.confirm(...)` direto: o `p-confirmDialog` dentro do
  // `app-dialog` está sob `*ngIf="isVisible"` e o `ConfirmationService` do PrimeNG
  // emite por `Subject` sem replay. Publicar antes de o host montar faz o diálogo
  // nascer depois da emissão e perdê-la — a modal não abre no primeiro clique,
  // silenciosamente. O `open()` monta e inscreve o host de forma síncrona antes de
  // publicar. Ver "Criação de Modais" no AGENTS.md.
  @ViewChild('previewDialog') previewDialog: DialogComponent | null = null;
  @ViewChild('finishDialog') finishDialog: DialogComponent | null = null;
  @ViewChild('unfinishDialog') unfinishDialog: DialogComponent | null = null;

  isMobile: boolean = false;
  isLoadingCompany = true;
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: '',
  };
  breadcrumbs: MenuItem[] = [];
  onboardingCompanyInformation: OnboardingCompanyInformation | null = null;
  metrics: AnalysisMetrics = buildAnalysisMetrics(null);
  stageMetrics: Record<string, StageMetrics> = {};
  documentLabels: Record<string, Record<string, string>> = {};
  submissionTooltips: Record<string, string> = {};
  analysisTooltips: Record<string, string> = {};
  statusLabels: { approved: string; rejected: string; pending: string } = {
    approved: '',
    rejected: '',
    pending: '',
  };
  noDocumentSentLabel = '';
  finalizeAnalysisLabel = '';
  approveAllLabel = '';
  isApprovingAll = false;

  /**
   * Notificar a empresa por e-mail ao finalizar é **opcional e opt-in**: começa
   * desmarcado e é resetado a cada abertura do diálogo, porque o envio é
   * irreversível e chega ao cliente.
   */
  sendEmailOnFinalize = false;
  sendEmailCheckbox: Checkbox = {
    label: 'Enviar e-mail de resultado aos usuários desta empresa',
    name: 'sendEmailOnFinalize',
    value: 'sendEmailOnFinalize',
    isChecked: false,
    isDisabled: false,
  };
  private expandedStages = new Set<string>();
  private hasLoadedOnce = false;
  companyId: string = '';
  notes: string | undefined = '';
  selectedDocumentAnalysisFile: OnboardingProcessStageSubmittedDocuments | null = null;
  selectedDocumentRequiredDocument: OnboardingProcessStageRequiredDocuments | null = null;
  selectedDocumentStage: OnboardingProcessStage | null = null;

  constructor(
    store: Store<AppState>,
    translateService: TranslateService,
    private toast: ToastHelperService,
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private dashboardService: DashboardService,
    private onboardingService: OnboardingService,
    private confirmDialogService: ConfirmDialogService,
    private dialogService: DialogService,
    private presentationService: DocumentAnalysisPresentationService
  ) {
    super(translateService, store);
    this.onboardingDocumentsTranslationService = new OnboardingDocumentsTranslationService(
      translateService
    );
  }
  onboardingDocumentsTranslationService: OnboardingDocumentsTranslationService;
  ngOnInit(): void {
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.DocumentAnalysis),
        routerLink: '/dashboard/document-analysis',
      },
      {
        label: this.t(this.tk.Management.CompanyName),
        routerLink: '/dashboard/document-analysis-company',
      },
    ];
    this.statusLabels = {
      approved: this.t(this.tk.Onboarding.Approved1),
      rejected: this.t(this.tk.Onboarding.RequestRejected),
      pending: this.t(this.tk.Onboarding.PendingAnalysis),
    };
    this.noDocumentSentLabel = this.t(this.tk.Onboarding.NoDocumentSent);
    this.finalizeAnalysisLabel = this.t(this.tk.Management.FinalizeAnalysis);
    this.approveAllLabel = this.t(this.tk.Management.ApproveAllDocuments);

    this.verifyIsMobile();
    this.getDocumentInfoById();
  }

  getDocumentInfoById(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.companyId = params['id'];
        this.utilsService.setIsLoading(true);
        this.onboardingService.getOnboardingComponyInformation(this.companyId).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            this.isLoadingCompany = false;
            if (response) {
              this.onboardingCompanyInformation = response;

              const companyName = response.corporateName?.trim();
              if (companyName && this.breadcrumbs.length > 1) {
                this.breadcrumbs = [
                  this.breadcrumbs[0],
                  { ...this.breadcrumbs[1], label: companyName },
                ];
              }

              const stagesOrder = [
                'CompanyDocuments',
                'GeneralDocuments',
                'BillingDocuments',
                'FinancialStatements',
                'Projects',
                'GuaranteeDocuments',
              ];

              // Sort the stages array based on the defined order
              if (this.onboardingCompanyInformation.onboardingProcess?.stages) {
                this.onboardingCompanyInformation.onboardingProcess.stages.sort((a, b) => {
                  const indexA = stagesOrder.indexOf(a.name);
                  const indexB = stagesOrder.indexOf(b.name);

                  // Handle cases where a stage name might not be in the order array
                  if (indexA === -1 && indexB === -1) return 0;
                  if (indexA === -1) return 1;
                  if (indexB === -1) return -1;

                  return indexA - indexB;
                });
              }

              this.metrics = buildAnalysisMetrics(
                this.onboardingCompanyInformation.onboardingProcess?.stages
              );

              const stages = this.onboardingCompanyInformation.onboardingProcess?.stages ?? [];

              const presentationMaps = this.presentationService.buildPresentationMaps(stages);
              this.documentLabels = presentationMaps.documentLabels;
              this.submissionTooltips = presentationMaps.submissionTooltips;
              this.analysisTooltips = presentationMaps.analysisTooltips;

              this.stageMetrics = stages.reduce<Record<string, StageMetrics>>((map, stage) => {
                map[stage.name] = buildStageMetrics(stage);

                return map;
              }, {});

              // Design: só a primeira etapa começa expandida — mas apenas no
              // primeiro carregamento. `handleApproveDocument`/`handleRejectDocument`
              // chamam este método de novo após cada ação; nesses refetches,
              // preserva-se o que o analista tinha aberto (descartando etapas que
              // deixaram de existir), em vez de resetar para a primeira etapa.
              const firstStageWithDocuments = stages.find(stage => stage.requiredDocuments.length);

              if (!this.hasLoadedOnce) {
                this.expandedStages = new Set(
                  firstStageWithDocuments ? [firstStageWithDocuments.name] : []
                );
                this.hasLoadedOnce = true;
              } else {
                const stageNames = new Set(stages.map(stage => stage.name));
                const kept = new Set([...this.expandedStages].filter(name => stageNames.has(name)));

                this.expandedStages = kept.size
                  ? kept
                  : new Set(firstStageWithDocuments ? [firstStageWithDocuments.name] : []);
              }
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            this.isLoadingCompany = false;
            console.error('getOnboardingInformationById error: ', error);
          },
          complete: () => {
            this.utilsService.setIsLoading(false);
            this.isLoadingCompany = false;
          },
        });
      }
    });
  }

  handleTableAction(
    submitedDocument: OnboardingProcessStageSubmittedDocuments,
    requiredDocument: OnboardingProcessStageRequiredDocuments,
    stage: OnboardingProcessStage
  ): void {
    if (submitedDocument) {
      this.selectedDocumentAnalysisFile = submitedDocument;
      this.selectedDocumentRequiredDocument = requiredDocument;
      this.selectedDocumentStage = stage;
      this.handleDocumentPreview();
    }
  }

  onSendEmailOnFinalizeChange(checkbox: Checkbox): void {
    this.sendEmailOnFinalize = !!checkbox.isChecked;
  }

  handleDocumentPreview(): void {
    this.notes = this.selectedDocumentAnalysisFile?.analysisNotes;
    this.previewDialog?.open('', '', '', '', '', 'document-preview', false, false);
  }

  saveOnboarding(item: CompanyOnboardingRequest): void {
    this.utilsService.setIsLoading(true);
    this.onboardingService
      .postOnboardingCompanyInformation(this.companyId, item as CompanyOnboardingRequest)
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(
            this.t(this.tk.Onboarding.OnboardingUpdated),
            this.t(this.tk.Onboarding.OnboardingSavedSuccessfully)
          );
          this.utilsService.setIsLoading(false);
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.error('Error updating onboarding: ', error);
          this.toast.showToastError(
            this.t(this.tk.Onboarding.ErrorUpdatingOnboarding),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  handleRejectDocument(item: OnboardingProcessStageSubmittedDocuments) {
    this.utilsService.setIsLoading(true);
    this.onboardingService
      .putOnboardingCompanyDocumentStatus(
        this.companyId,
        item.id as string,
        DocumentStatus.Rejected,
        this.notes
      )
      .subscribe({
        next: () => {
          this.utilsService.setIsLoading(false);
          this.toast.showToastSuccess(
            this.t(this.tk.Management.DocumentRejected),
            this.t(this.tk.Management.DocumentRejectedSuccessfully)
          );
          this.confirmDialogService.close();
          this.getDocumentInfoById();
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.error('Error rejecting document: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorRejectingDocument),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
          this.confirmDialogService.close();
        },
      });
  }

  handleApproveDocument(item: OnboardingProcessStageSubmittedDocuments) {
    this.utilsService.setIsLoading(true);
    this.onboardingService
      .putOnboardingCompanyDocumentStatus(
        this.companyId,
        item.id as string,
        DocumentStatus.Approved,
        this.notes
      )
      .subscribe({
        next: () => {
          this.utilsService.setIsLoading(false);
          this.toast.showToastSuccess(
            this.t(this.tk.Management.DocumentApproved),
            this.t(this.tk.Management.DocumentApprovedSuccessfully)
          );
          this.confirmDialogService.close();
          this.getDocumentInfoById();
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.error('Error approving document: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorApprovingDocument),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
          this.confirmDialogService.close();
        },
      });
  }

  /**
   * "Aprovar todos": aprova em uma confirmação todos os documentos PENDENTES da empresa.
   *
   * Não substitui a aprovação individual, que continua sendo o caminho de quando há ressalva num
   * documento. Também não finaliza a análise nem envia e-mail — `sendEmailOnFinalize` não é tocado
   * aqui, de propósito: notificar o cliente é irreversível e segue sendo opt-in do "Finalizar".
   */
  handleApproveAllPending(): void {
    if (this.isApprovingAll) {
      return;
    }

    const pendingIds = collectPendingDocumentIds(
      this.onboardingCompanyInformation?.onboardingProcess?.stages
    );

    if (!pendingIds.length) {
      return;
    }

    // O rotulo cai numa coluna de largura FIXA no `modal-confirm` compartilhado
    // (`modal-confirm.component.scss`: `.modal-confirm-details-label { flex: 0 0 80px }`), pensada
    // para rotulos curtos do tipo "ID"/"Tipo"/"Contrato". Rotulo longo quebra em duas linhas e, com
    // o `align-items: baseline` da linha, o valor alinha na PRIMEIRA delas: "Documentos a aprovar"
    // + "5" era lido na tela como "DOCUMENTOS 5 A APROVAR". Por isso o rotulo e curto e a contagem
    // vai no VALOR, que e o texto em destaque. Nao resolva isso mexendo nos 80px: a modal e
    // compartilhada com outras telas.
    const documentWord = pendingIds.length === 1 ? 'documento' : 'documentos';
    const descriptionDetails: ModalConfirmDetailItem[] = [
      { label: 'Aprovar', value: `${pendingIds.length} ${documentWord}` },
    ];

    // Decisão explícita do analista não é sobrescrita por ação em massa — e a confirmação diz isso
    // ANTES de executar, em vez de deixar a pessoa descobrir depois (critério 4 do card).
    if (this.metrics.rejected > 0) {
      const rejectedWord = this.metrics.rejected === 1 ? 'rejeitado' : 'rejeitados';

      descriptionDetails.push({
        label: 'Manter',
        value: `${this.metrics.rejected} ${rejectedWord}`,
      });
    }

    const modalRef = this.dialogService.open(ModalConfirmComponent, {
      data: {
        title: this.t(this.tk.Management.ApproveAllDocuments),
        description: this.t(this.tk.Management.ApproveAllDocumentsConfirmation),
        descriptionDetails,
        btnFirst: this.t(this.tk.General.Cancel),
        btnSecond: this.t(this.tk.General.Confirm),
      },
    });

    if (modalRef) {
      modalRef.onClose.subscribe({
        next: result => {
          if (result?.confirmed) {
            this.approveAllPendingDocuments(pendingIds);
          }
        },
      });
    }
  }

  private approveAllPendingDocuments(documentIds: string[]): void {
    this.isApprovingAll = true;
    this.utilsService.setIsLoading(true);

    this.onboardingService
      .putOnboardingCompanyDocumentsApproval(this.companyId, documentIds)
      .subscribe({
        next: report => {
          this.isApprovingAll = false;
          this.utilsService.setIsLoading(false);
          this.reportBulkApproval(report);
          // UM refetch, no fim. Ele passa pelo mesmo caminho que preserva as etapas que o analista
          // tinha expandido (ver getDocumentInfoById), então a tela não volta para a primeira etapa.
          this.getDocumentInfoById();
        },
        error: error => {
          this.isApprovingAll = false;
          this.utilsService.setIsLoading(false);
          console.error('Error approving all documents: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorApprovingDocument),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
          // O endpoint é deliberadamente não atômico: timeout/conexão caída/504 aborta a requisição
          // mas o servidor segue e pode ter aplicado parte (ou tudo) das aprovações. Sem refetch, a
          // tela ficaria mostrando pendências que já não existem.
          this.getDocumentInfoById();
        },
      });
  }

  /**
   * Traduz o relatório do backend em feedback. Falha parcial NUNCA vira sucesso silencioso: se um
   * documento não pôde ser aprovado, a tela diz quantos e quais (critério 8 do card). Documento
   * `Skipped` (pedido mas não tocado) também é reportado — no detalhe do mesmo toast, sem mudar a
   * severidade do ramo.
   */
  private reportBulkApproval(report: BulkApproveDocumentsResult | null): void {
    const applied = report?.applied ?? 0;
    const failedItems = (report?.items ?? []).filter(item => item.outcome === 'Failed');
    // O contador `failed` do backend é a fonte de verdade; `items` só existe para nomear os
    // documentos. Disparar o aviso só quando `items` tem entrada 'Failed' deixaria a tela mentir
    // se o backend mandar `failed > 0` com `items` vazio/truncado/outcome não reconhecido.
    const failedCount = Math.max(report?.failed ?? 0, failedItems.length);

    // `skipped` era o silêncio deste relatório: documento que a tela pediu e o backend não tocou.
    // São dois os motivos, e só dois: o documento já tinha decisão (aprovado ou rejeitado — típico de
    // outro analista ter decidido entre o carregamento da tela e a confirmação), ou o id não pertence
    // àquela empresa. Sem dizer nada, o analista via "N aprovado(s)" com pendência sobrando no rodapé
    // e nenhuma explicação. Entra como sufixo do detalhe do MESMO toast de cada ramo (não um toast
    // extra) e NÃO muda a severidade: lote todo aplicado + skipped segue sucesso.
    // Rastro (card 2904): reenvio anual "superado" NÃO é mais motivo de `skipped` — o lote passou a
    // aprovar todo pendente da empresa, sem a dedupe anual que antes o deixava de fora.
    const skippedCount = report?.skipped ?? 0;
    const skippedDetail =
      skippedCount > 0
        ? this.t(this.tk.Management.ApproveAllDocumentsSkipped, { skipped: skippedCount })
        : '';

    if (failedCount > 0) {
      const MAX_NAMES_SHOWN = 5;
      const failedNames = failedItems.map(item => item.documentName || item.documentId);
      const shownNames = failedNames.slice(0, MAX_NAMES_SHOWN);
      const remainingNames = failedNames.length - shownNames.length;
      const names =
        remainingNames > 0
          ? `${shownNames.join(', ')} e ${remainingNames} outros`
          : shownNames.join(', ');

      this.toast.showToastWarn(
        this.t(this.tk.Management.ApproveAllDocumentsPartial, {
          applied,
          failed: failedCount,
        }),
        [names, skippedDetail].filter(Boolean).join(' · '),
        10000
      );

      return;
    }

    // `applied === 0` sem falha registrada acontece com `report == null` (corpo vazio/204, via
    // `Observable<T | null>`) ou quando todos os ids vieram `Skipped` — outro analista já decidiu
    // esses documentos, ou um reenvio após timeout que na verdade já tinha aplicado tudo. Nenhum
    // dos dois é "documento aprovado com sucesso": não pode cair no toast de sucesso.
    if (applied === 0) {
      this.toast.showToastWarn(
        this.t(this.tk.Management.ApproveAllDocumentsNoneApplied),
        skippedDetail
      );

      return;
    }

    this.toast.showToastSuccess(
      this.t(this.tk.Management.DocumentApproved),
      [this.t(this.tk.Management.ApproveAllDocumentsSuccess, { applied }), skippedDetail]
        .filter(Boolean)
        .join(' · ')
    );
  }

  handleDocumentAnalysisAction(
    item: OnboardingProcessStageSubmittedDocuments,
    action: 'reject' | 'finish' | 'undone' | 'download'
  ): void {
    switch (action) {
      case 'reject': {
        const modalRef = this.dialogService.open(ModalConfirmComponent, {
          data: {
            title: this.t(this.tk.Management.RejectDocument),
            description: this.t(this.tk.Management.AreYouSureYouWantToReject),
            btnFirst: this.t(this.tk.General.Cancel),
            btnSecond: this.t(this.tk.General.Confirm),
          },
        });

        if (modalRef) {
          modalRef.onClose.subscribe({
            next: result => {
              if (result?.confirmed) {
                this.handleRejectDocument(item);
              }
            },
          });
        }

        break;
      }
      case 'finish': {
        const modalRef = this.dialogService.open(ModalConfirmComponent, {
          data: {
            title: this.t(this.tk.Management.ApproveDocument),
            description: this.t(this.tk.Management.ApproveDocumentConfirmation),
            btnFirst: this.t(this.tk.General.Cancel),
            btnSecond: this.t(this.tk.General.Confirm),
          },
        });

        if (modalRef) {
          modalRef.onClose.subscribe({
            next: result => {
              if (result?.confirmed) {
                this.handleApproveDocument(item);
              }
            },
          });
        }
        break;
      }
      case 'undone':
        this.confirmDialogService
          .confirm(
            this.t(this.tk.Management.TheReviewWillBeSavedNSentToCustomer),
            this.t(this.tk.Management.CheckBeforeApproving),
            this.t(this.tk.General.Ok),
            '',
            '',
            '',
            false
          )
          .then(() => {
            this.confirmDialogService.close();
          });
        break;
      case 'download':
        window.open(item.documentLink, '_blank');
        break;
    }
  }

  cancelRegister(): void {
    this.router.navigate(['dashboard/document-analysis']);
  }

  verifyIsMobile() {
    if (this.store) {
      this.store.select(selectIsMobile).subscribe(isMobile => (this.isMobile = isMobile));
    }
  }

  finalizeDocumentAnalysis(): void {
    if (this.hasDocumentsToAnalysis) {
      this.unfinishDialog
        ?.open(
          this.t(this.tk.Management.CheckBeforeApproving),
          this.t(this.tk.Management.ThereAreUnratedFiles),
          this.t(this.tk.General.Ok),
          '',
          '',
          'document-unfinish',
          false
        )
        .then(() => {
          this.confirmDialogService.close();
        });
    } else {
      // Reseta a cada abertura: notificar o cliente é irreversível, então a opção
      // nunca vem herdada de uma tentativa anterior.
      this.sendEmailOnFinalize = false;
      this.sendEmailCheckbox = { ...this.sendEmailCheckbox, isChecked: false };

      this.finishDialog
        ?.open(
          '',
          this.t(this.tk.Management.AreYouSureYouWantToFinish),
          this.t(this.tk.User.Finish),
          this.t(this.tk.General.Review),
          '',
          'document-finish',
          true
        )
        .then(response => {
          if (response) {
            this.onboardingService
              .putSendEmailApproveOrReject(this.companyId, this.sendEmailOnFinalize)
              .subscribe({
                next: result => {
                  this.toast.showToastSuccess(
                    this.t(this.tk.Management.DocumentApproved),
                    this.t(this.tk.Management.DocumentApprovedSuccessfully)
                  );

                  // O e-mail foi pedido mas ninguém foi notificado: a empresa não
                  // tem usuário cadastrado. A análise foi finalizada de verdade —
                  // por isso é aviso, não erro —, mas o analista precisa saber que
                  // o cliente não recebeu nada.
                  if (result?.emailRequested && result.usersNotified === 0) {
                    this.toast.showToastWarn(
                      'Nenhum usuário notificado',
                      'A análise foi finalizada, mas esta empresa não tem usuários cadastrados para receber o e-mail.'
                    );
                  }

                  this.confirmDialogService.close();
                  this.router.navigate(['dashboard/document-analysis']);
                },
                error: error => {
                  this.utilsService.setIsLoading(false);
                  console.error('Error approving document: ', error);
                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorSendingEmailAnalysisResults),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                  this.confirmDialogService.close();
                },
              });
          }
        });
    }
  }

  /**
   * Há documento sem decisão? Antes lia `documetAnalysisFile`, que nunca era
   * populado — o alerta de análise incompleta em `finalizeDocumentAnalysis()`
   * portanto nunca disparava.
   */
  get hasDocumentsToAnalysis(): boolean {
    return this.metrics.pending > 0;
  }

  isStageExpanded(stageName: string): boolean {
    return this.expandedStages.has(stageName);
  }

  toggleStage(stageName: string): void {
    const expanded = new Set(this.expandedStages);

    if (expanded.has(stageName)) {
      expanded.delete(stageName);
    } else {
      expanded.add(stageName);
    }

    this.expandedStages = expanded;
  }

  get hasExpandedStage(): boolean {
    return this.expandedStages.size > 0;
  }

  toggleAllStages(): void {
    if (this.hasExpandedStage) {
      this.expandedStages = new Set<string>();

      return;
    }

    const stages = this.onboardingCompanyInformation?.onboardingProcess?.stages ?? [];
    this.expandedStages = new Set(stages.map(stage => stage.name));
  }
}
